import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ReverLoading, toast } from '@itsrever/design-system'
import type { PbWithdrawalRequest } from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import { useWithdrawalSettings } from '@/hooks/use-withdrawal-settings'
import {
  WITHDRAWALS_PAGE_SIZE,
  useSetWithdrawalClosed,
  useWithdrawalRequests
} from '@/hooks/use-withdrawal-requests'
import NotFound from '@/ui/pages/404.page'
import { ListTabs, useListTabsPage } from '@/ui/shared/list-tabs'
import { ExportWithdrawalsButton } from './withdrawals/export-withdrawals-button'
import { useWithdrawalColumns } from './withdrawals/withdrawals-columns'

const ALL = 'all'

export default function WithdrawalsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const ecommerceId = useAppSelector((s) => s.generalData.selectedEcommerce)
  const role = useAppSelector((s) => s.userApi.getMe.response.user?.role)
  // A reviewer only sees its warehouses' returns, and these requests belong to no warehouse.
  const settings = useWithdrawalSettings(
    role === Roles.Reviewer ? undefined : ecommerceId
  )
  const { offsetFor } = useListTabsPage(ALL, WITHDRAWALS_PAGE_SIZE)
  const list = useWithdrawalRequests(
    settings.enabled ? ecommerceId : undefined,
    offsetFor(ALL)
  )
  const setClosed = useSetWithdrawalClosed(ecommerceId ?? undefined)
  const { mutate } = setClosed
  const toggleClosed = useCallback(
    (w: PbWithdrawalRequest) =>
      mutate(
        { id: w.id ?? '', closed: !w.closed_at },
        {
          onError: () =>
            toast({ text: t('withdrawals.close_error'), variant: 'error' })
        }
      ),
    [mutate, t]
  )
  const columns = useWithdrawalColumns({
    canClose: role === Roles.ReadWrite,
    pendingId: setClosed.isPending ? setClosed.variables?.id : undefined,
    toggle: toggleClosed
  })

  if (settings.loading) return <ReverLoading />
  if (!settings.enabled || !ecommerceId) return <NotFound />

  const openReturn = (w: PbWithdrawalRequest) => {
    if (w.process_id) navigate(`/returns/orders/${w.process_id}`)
  }

  return (
    <div className="relative h-full">
      <div className="min-h-full bg-neutral-100/10 p-4">
        <div className="mb-4 flex items-center justify-between gap-3.5 border-b border-solid border-neutral-200 pb-4">
          <WithdrawalIcon />
          <header className="mr-auto space-y-1">
            <h1 className="font-[Roobert] text-lg font-medium leading-6 text-neutral-900">
              {t('withdrawals.title')}
            </h1>
            <h2 className="font-[Roobert] text-sm font-normal leading-5 text-neutral-600">
              {t('withdrawals.subtitle')}
            </h2>
          </header>
          <ExportWithdrawalsButton ecommerceId={ecommerceId} />
        </div>
        <ListTabs
          tabs={[
            {
              value: ALL,
              label: t('withdrawals.all'),
              rows: list.data && (list.data.withdrawal_requests ?? []),
              count: Number(list.data?.rowcount ?? 0),
              isPending: list.isPending,
              isFetching: list.isFetching
            }
          ]}
          allTab={ALL}
          pageSize={WITHDRAWALS_PAGE_SIZE}
          columns={columns}
          onRowClick={openReturn}
          pinFirstColumn
        />
      </div>
    </div>
  )
}

// The same circle as the Orders and Items headers, so the Returns pages read as one section.
const WithdrawalIcon = () => (
  <div className="flex select-none items-center justify-center overflow-hidden rounded-full border border-solid border-neutral-200 bg-white p-3 shadow-sm">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-neutral-700"
      aria-hidden
    >
      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
      <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
      <path d="M10 12l4 4m0 -4l-4 4" />
    </svg>
  </div>
)
